const clone = (value) => value === undefined
  ? undefined
  : JSON.parse(JSON.stringify(value));

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) deepFreeze(child);
  return Object.freeze(value);
}

function assertPlainObject(value, name) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object`);
  }
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    throw new TypeError(`${name} must be a plain object`);
  }
  return value;
}

function assertVersion(value, name) {
  if (!Number.isInteger(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative integer`);
  }
  return value;
}

function normalizeTimestamp(value, fallback) {
  if (typeof value !== 'string' || Number.isNaN(Date.parse(value))) return fallback;
  return new Date(value).toISOString();
}

export class SaveMigrationRegistry {
  constructor({ currentVersion }) {
    this.currentVersion = assertVersion(currentVersion, 'currentVersion');
    this.migrations = new Map();
  }

  register({ fromVersion, toVersion, migrate }) {
    const normalizedFrom = assertVersion(fromVersion, 'fromVersion');
    const normalizedTo = assertVersion(toVersion, 'toVersion');
    if (normalizedTo <= normalizedFrom) {
      throw new RangeError('toVersion must be greater than fromVersion');
    }
    if (typeof migrate !== 'function') throw new TypeError('migrate must be a function');
    if (this.migrations.has(normalizedFrom)) {
      throw new RangeError(`A migration from version ${normalizedFrom} is already registered`);
    }
    this.migrations.set(normalizedFrom, {
      fromVersion: normalizedFrom,
      toVersion: normalizedTo,
      migrate,
    });
    return this;
  }

  migrate(value) {
    let current = clone(assertPlainObject(value, 'save'));
    const fromVersion = assertVersion(current.version, 'save.version');
    if (fromVersion > this.currentVersion) {
      throw new RangeError(`Save version ${fromVersion} is newer than supported version ${this.currentVersion}`);
    }

    const applied = [];
    let guard = 0;
    while (current.version < this.currentVersion) {
      if (guard++ > 100) throw new RangeError('Save migration cycle detected');
      const migration = this.migrations.get(current.version);
      if (!migration) {
        throw new RangeError(`No save migration is registered from version ${current.version}`);
      }
      const next = clone(assertPlainObject(migration.migrate(clone(current)), 'migrated save'));
      if (next.version !== migration.toVersion) {
        throw new RangeError(
          `Migration from version ${migration.fromVersion} must produce version ${migration.toVersion}`,
        );
      }
      applied.push({
        fromVersion: migration.fromVersion,
        toVersion: migration.toVersion,
      });
      current = next;
    }

    return deepFreeze({
      value: current,
      fromVersion,
      toVersion: current.version,
      applied,
    });
  }
}

export function createDefaultSaveMigrationRegistry() {
  const registry = new SaveMigrationRegistry({ currentVersion: 1 });
  registry.register({
    fromVersion: 0,
    toVersion: 1,
    migrate: (legacy) => {
      const session = clone(legacy.session ?? {});
      const epoch = '1970-01-01T00:00:00.000Z';
      const createdAt = normalizeTimestamp(
        legacy.createdAt ?? legacy.metadata?.createdAt,
        epoch,
      );
      const updatedAt = normalizeTimestamp(
        legacy.updatedAt ?? legacy.metadata?.updatedAt,
        createdAt,
      );
      const metadata = clone(legacy.metadata ?? {});
      delete metadata.createdAt;
      delete metadata.updatedAt;
      const nextTick = session.simulation?.nextTick
        ?? session.simulationNextTick
        ?? legacy.nextTick
        ?? 0;
      delete session.simulationNextTick;

      return {
        format: 'serpent-born-wild-save',
        version: 1,
        createdAt,
        updatedAt,
        metadata: {
          ...metadata,
          migratedFromVersion: 0,
        },
        session: {
          ...session,
          environment: session.environment ?? legacy.environment ?? {
            activeScenario: null,
            timeOfDay: 'day',
            weather: 'clear',
          },
          simulation: { nextTick },
          snakeState: session.snakeState ?? null,
        },
      };
    },
  });
  return registry;
}
